import { FC, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import moment from 'moment'
import Button from 'src/components/Button'
import { ReactComponent as CloseIcon } from 'src/assets/svg/close.svg'
import { ReactComponent as CommentsIcon } from 'src/assets/svg/comments.svg'
import { ReactComponent as EyeIcon } from 'src/assets/svg/eye.svg'
import { ReactComponent as ReactionsIcon } from 'src/assets/svg/reactions.svg'
import { ReactComponent as RepostsIcon } from 'src/assets/svg/reposts.svg'
import { ReactComponent as SharesIcon } from 'src/assets/svg/shares.svg'
import { ReactComponent as ExternalLinkIcon } from 'src/assets/svg/external-link.svg'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { formatOptionalCount, numberWithCommas } from 'src/utils/numberFormat'
import { IndividualPost, PostComment } from 'src/services/__generated__/api'
import styles from './postDetailsModal.module.scss'

type SentimentTab = 'all' | 'positive' | 'neutral' | 'negative'

const SENTIMENT_COLORS = {
  negative: '#FF6833',
  neutral: '#C7CED1',
  positive: '#8080FF',
}

interface CommentItemProps {
  comment: PostComment
}

const CommentItem: FC<CommentItemProps> = ({ comment }) => {
  const sentiment = comment.sentiment || 'neutral'
  return (
    <div className={styles.commentItem}>
      <div className={styles.commentHeader}>
        <span className={styles.commentAuthor}>{comment.author}</span>
        <span
          className={styles.sentimentBadge}
          style={{
            backgroundColor: SENTIMENT_COLORS[sentiment] + '20',
            color: SENTIMENT_COLORS[sentiment],
          }}
        >
          {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
        </span>
      </div>
      <p className={styles.commentContent}>{comment.content}</p>
      {/* Date only: manually inserted comments carry a placeholder 00:00 time. */}
      {comment.timestamp && moment(comment.timestamp).isValid() && (
        <span className={styles.commentTimestamp}>
          {moment(comment.timestamp).format('D MMM YYYY')}
        </span>
      )}
    </div>
  )
}

interface PostDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  post: IndividualPost
}

export const PostDetailsModal: FC<PostDetailsModalProps> = ({
  isOpen,
  onClose,
  post,
}) => {
  const [activeTab, setActiveTab] = useState<SentimentTab>('all')
  const comments = post.comments || []
  const breakdown = post.commentsBreakdown || {}
  const platform = post.platform || ''
  const postUrl = (post as IndividualPost & { postUrl?: string }).postUrl

  const filteredComments = useMemo(() => {
    const result =
      activeTab === 'all'
        ? comments
        : comments.filter((comment) => comment.sentiment === activeTab)

    return [...result].sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return dateB - dateA
    })
  }, [comments, activeTab])

  const handleClose = () => {
    setActiveTab('all')
    onClose()
  }

  if (!isOpen) return null

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={handleClose} />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Post Details</h2>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.content}>
          {post.imageUrl && (
            <div className={styles.imageWrapper}>
              <img
                alt="Post"
                className={styles.postImage}
                src={post.imageUrl}
              />
            </div>
          )}

          <p className={styles.postContent}>{post.content}</p>

          <div className={styles.metricsWrapper}>
            <div className={styles.metrics}>
              <span className={styles.metricItem}>
                <ReactionsIcon className={styles.metricIcon} />
                {numberWithCommas(post.reactions || 0)}
              </span>
              <span className={styles.metricItem}>
                <CommentsIcon className={styles.metricIcon} />
                {numberWithCommas(post.commentsCount || 0)}
                <span className={styles.breakdown}>
                  (
                  <span
                    className={styles.dot}
                    style={{ backgroundColor: SENTIMENT_COLORS.positive }}
                  />
                  {breakdown.positive || 0}
                  <span
                    className={styles.dot}
                    style={{ backgroundColor: SENTIMENT_COLORS.neutral }}
                  />
                  {breakdown.neutral || 0}
                  <span
                    className={styles.dot}
                    style={{ backgroundColor: SENTIMENT_COLORS.negative }}
                  />
                  {breakdown.negative || 0})
                </span>
              </span>
              <span className={styles.metricItem} title="Shares">
                <SharesIcon className={styles.metricIcon} />
                {numberWithCommas(post.shares || 0)}
              </span>
              {/* Instagram reposts, only when reported (see PostCard). */}
              {typeof post.reposts === 'number' && (
                <span className={styles.metricItem} title="Reposts">
                  <RepostsIcon className={styles.metricIcon} />
                  {numberWithCommas(post.reposts)}
                </span>
              )}
              <span className={styles.metricItem}>
                <EyeIcon className={styles.metricIcon} />
                {post.viewsApproximate ? '~' : ''}
                {formatOptionalCount(post.views)}
              </span>
            </div>

            <div className={styles.additionalInfo}>
              <a
                className={styles.seePostButton}
                href={postUrl || '#'}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className={styles.platformIcon}>
                  {SOCIAL_ICONS[platform.toLowerCase()]}
                </span>
                <span className={styles.seePostText}>See post</span>
                <ExternalLinkIcon className={styles.externalIcon} />
              </a>
            </div>
          </div>

          <div className={styles.commentsSection}>
            <div className={styles.commentsHeader}>
              <span className={styles.commentsTitle}>Comments</span>
              <div className={styles.tabs}>
                {(
                  ['all', 'positive', 'neutral', 'negative'] as SentimentTab[]
                ).map((tab) => (
                  <button
                    className={classNames(styles.tab, {
                      [styles.active]: activeTab === tab,
                    })}
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    type="button"
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.commentsList}>
              {filteredComments.length === 0 ? (
                <div className={styles.emptyComments}>No comments found</div>
              ) : (
                filteredComments.map((comment, index) => (
                  <CommentItem comment={comment} key={index} />
                ))
              )}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button background="transparent" onClick={handleClose}>
            Cancel
          </Button>
          <Button background="malibu" onClick={handleClose}>
            Done
          </Button>
        </div>
      </div>
    </>,
    document.body
  )
}
